import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "X-Requested-With": "XMLHttpRequest" },
});

// Attach Bearer token fallback from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/** Avvia un'elaborazione IA in background e attende il risultato (il proxy chiude le richieste lunghe). */
export async function runAiJob(tipo, payload = {}, { timeoutMs = 300000, intervalMs = 0 } = {}) {
  const { data: start } = await api.post("/ai/jobs", { tipo, payload });
  const scadenza = Date.now() + timeoutMs;
  // Attesa progressiva: reattiva sui lavori brevi, leggera su quelli lunghi.
  let attesa = intervalMs || 700;
  while (Date.now() < scadenza) {
    await new Promise((r) => setTimeout(r, attesa));
    if (!intervalMs) attesa = Math.min(Math.round(attesa * 1.4), 2500);
    const { data: job } = await api.get(`/ai/jobs/${start.job_id}`);
    if (job.stato === "done") return job.risultato;
    if (job.stato === "error") throw new Error(job.errore || "Errore IA");
  }
  throw new Error("L'elaborazione IA sta richiedendo troppo tempo. Riprova.");
}

// ---- Stato server (banner "il server non risponde") ----
const serverStatusListeners = new Set();
let serverDown = false;

function setServerDown(value) {
  if (serverDown === value) return;
  serverDown = value;
  serverStatusListeners.forEach((cb) => cb(value));
}

/** Iscrive un listener allo stato del server. Ritorna la funzione di unsubscribe. */
export function onServerStatus(cb) {
  serverStatusListeners.add(cb);
  cb(serverDown);
  return () => serverStatusListeners.delete(cb);
}

const GATEWAY = [502, 503, 504, 520, 521, 522, 523, 524];
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
let healthProbe = null;

/** Conferma un guasto SOLO con due /health falliti di seguito: mai banner per un singolo errore di rete. */
async function confermaGuasto() {
  if (healthProbe) return healthProbe;
  healthProbe = (async () => {
    for (let i = 0; i < 2; i += 1) {
      try {
        await axios.get(`${API}/health`, { timeout: 6000 });
        setServerDown(false);
        return;
      } catch {
        await sleep(1500);
      }
    }
    if (typeof navigator === "undefined" || navigator.onLine !== false) setServerDown(true);
  })();
  try { await healthProbe; } finally { healthProbe = null; }
}

// ---- cache GET in memoria (30 s) + deduplica richieste identiche in volo: pagine istantanee al ritorno, nessuna doppia chiamata.
// Qualsiasi POST/PUT/PATCH/DELETE svuota la cache, così dopo un salvataggio i dati sono sempre freschi.
const CACHE_TTL = 30 * 1000;
const cache = new Map();
const inFlight = new Map();
const cacheKey = (cfg) => `${cfg.url}|${cfg.params ? JSON.stringify(cfg.params) : ""}`;
const cacheable = (cfg) => (cfg.method || "get").toLowerCase() === "get" && (!cfg.responseType || cfg.responseType === "json") && cfg.cache !== false && !/\/ai\/jobs\//.test(cfg.url || "");
export const invalidateCache = () => { cache.clear(); };

api.interceptors.request.use((cfg) => {
  const isGet = (cfg.method || "get").toLowerCase() === "get";
  if (!isGet) { cache.clear(); return cfg; }
  if (cfg.responseType && cfg.responseType !== "json") return cfg;
  const key = cacheKey(cfg);
  if (cacheable(cfg)) {
    const hit = cache.get(key);
    if (hit && Date.now() - hit.t < CACHE_TTL) {
      cfg.adapter = () => Promise.resolve({ ...hit.res, config: cfg, request: {}, __fromCache: true });
      return cfg;
    }
  }
  const pending = inFlight.get(key);
  if (pending) {
    cfg.adapter = () => pending.then((res) => ({ ...res, config: cfg }));
    return cfg;
  }
  // registro la richiesta "in volo" subito (non quando parte l'adapter): due chiamate identiche nello stesso tick fanno una sola richiesta
  let risolvi, rifiuta;
  const p = new Promise((res, rej) => { risolvi = res; rifiuta = rej; });
  p.catch(() => {});
  inFlight.set(key, p);
  const base = axios.getAdapter(axios.defaults.adapter);
  cfg.adapter = (c) => base(c).then((r) => { risolvi(r); return r; }, (e) => { rifiuta(e); throw e; }).finally(() => { if (inFlight.get(key) === p) inFlight.delete(key); });
  return cfg;
});

api.interceptors.response.use(
  (response) => {
    setServerDown(false);
    const cfg = response.config || {};
    if (cacheable(cfg) && !response.__fromCache) {
      const key = cacheKey(cfg);
      cache.set(key, { t: Date.now(), res: { data: response.data, status: response.status, statusText: response.statusText, headers: response.headers } });
      inFlight.delete(key);
    }
    return response;
  },
  async (error) => {
    if (axios.isCancel?.(error) || error.code === "ERR_CANCELED") return Promise.reject(error);
    const cfg = error.config || {};
    if (cfg.url) inFlight.delete(cacheKey(cfg));
    const status = error.response?.status;
    if (status === 402 && !window.location.pathname.startsWith("/abbonamento")) window.location.assign("/abbonamento");
    const timeout = error.code === "ECONNABORTED";
    const transient = (!error.response && !timeout) || GATEWAY.includes(status);
    const method = (cfg.method || "get").toLowerCase();
    const idempotente = method === "get" || method === "head" || method === "delete";
    cfg.__retry = cfg.__retry || 0;
    // riprova silenziosa (2 tentativi con attesa crescente) per i micro-blackout di rete / risveglio del server
    if (transient && idempotente && cfg.__retry < 2 && !(cfg.data instanceof FormData)) {
      cfg.__retry += 1;
      await sleep(600 * cfg.__retry);
      return api.request(cfg);
    }
    if (transient) confermaGuasto();
    else if (status >= 500 && idempotente && !cfg.silenzioso) segnalaErroreServer(status, cfg.url);
    return Promise.reject(error);
  }
);

// un solo avviso ogni 20 s per gli errori 5xx delle letture: così le tabelle vuote per errore non passano per "nessun dato"
let ultimoAvviso5xx = 0;
function segnalaErroreServer(status, url) {
  const ora = Date.now();
  if (ora - ultimoAvviso5xx < 20000) return;
  ultimoAvviso5xx = ora;
  import("sonner").then(({ toast }) => toast.error("Il server ha risposto con un errore: alcuni dati potrebbero non essere aggiornati. Ricarica la pagina tra qualche secondo.", { id: "err-5xx", duration: 8000, description: `${status} · ${String(url || "").replace(/^.*\/api\//, "")}` })).catch(() => {});
}

// keep-alive: mentre l'app è aperta e visibile, un ping leggero ogni 4 minuti tiene il server sveglio
if (typeof window !== "undefined") {
  const ping = () => { if (document.visibilityState === "visible") axios.get(`${API}/health`, { timeout: 8000 }).then(() => setServerDown(false)).catch(() => {}); };
  setInterval(ping, 4 * 60 * 1000);
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") ping(); });
  window.addEventListener("online", ping);
}

export default api;

export function filenameFromHeaders(headers) {
  const cd = headers?.["content-disposition"] || "";
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (star) { try { return decodeURIComponent(star[1].trim()); } catch { /* fallthrough */ } }
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  return plain ? plain[1].trim() : "";
}

export function triggerDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "documento";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Download a file via authenticated request (Bearer header) as a Blob and trigger browser download.
 * Il nome file arriva dal server (Content-Disposition: "Tipo Committente del GG_MM_AAAA"); `filename` è solo fallback.
 */
export async function apiDownload(path, filename) {
  const resp = await api.get(path, { responseType: "blob" });
  const blob = new Blob([resp.data], { type: resp.headers["content-type"] || "application/pdf" });
  triggerDownload(blob, filenameFromHeaders(resp.headers) || filename);
  return blob;
}

/** Scarica un blob protetto senza avviare il download: restituisce { blob, filename } (nome dal server). */
export async function apiFetchBlob(path) {
  const resp = await api.get(path, { responseType: "blob" });
  return { blob: resp.data, filename: filenameFromHeaders(resp.headers), contentType: resp.headers["content-type"] || "" };
}

/**
 * Fetch a protected image resource as blob-url for use in <img src="">.
 * Caller MUST revoke the URL when the image unmounts.
 */
export async function apiImageBlobUrl(path) {
  const resp = await api.get(path, { responseType: "blob" });
  return window.URL.createObjectURL(resp.data);
}

/**
 * Fetch any authenticated resource (PDF, etc.) as blob-url for use in <iframe src="">.
 * Caller MUST revoke the URL when done.
 */
export async function apiFetchBlobUrl(path, mimeType) {
  const resp = await api.get(path, { responseType: "blob" });
  const blob = new Blob([resp.data], { type: mimeType || resp.headers["content-type"] || "application/octet-stream" });
  return window.URL.createObjectURL(blob);
}

export function formatApiError(detail) {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/** Messaggio amichevole in italiano per qualunque errore axios, anche quando il server non risponde. */
export function friendlyAxiosError(e) {
  const status = e?.response?.status;
  if (!e?.response || [502, 503, 504, 520, 521, 522, 523, 524].includes(status)) {
    return "Il server non risponde in questo momento. I tuoi dati sono al sicuro: attendi qualche istante e riprova.";
  }
  return formatApiError(e?.response?.data?.detail) || e?.message || "Si è verificato un errore. Riprova.";
}
